import {
  User,
  ActionCodeSettings,
  sendEmailVerification,
  updatePassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { auth } from "@/firebaseConfig";

// Action code settings for email verification
// Use current origin to support whitelabel subdomains
const getActionCodeSettings = (): ActionCodeSettings => {
  const origin = window.location.origin;
  
  // For localhost development, Firebase only authorizes 'localhost', not 'subdomain.localhost'
  // So we need to strip the subdomain for local development
  let continueUrl = `${origin}/verify-email-reminder?verified=true`;
  
  if (origin.includes('.localhost:')) {
    // Replace subdomain.localhost with just localhost for Firebase Auth
    // Example: http://bushfire.localhost:3001 -> http://localhost:3001
    const port = origin.split(':')[2];
    continueUrl = `http://localhost:${port}/verify-email-reminder?verified=true`;
  }
  
  return {
    url: continueUrl,
    handleCodeInApp: true,
    // Only include dynamicLinkDomain in production
    ...(origin.includes('localhost') ? {} : { dynamicLinkDomain: "zurilink.page.link" }),
  };
};

/**
 * Set the language for Firebase Auth emails
 * @param languageCode - Language code (e.g., 'en', 'es', 'fr') or null to use device language
 */
export const setAuthLanguage = (languageCode: string | null = null) => {
  try {
    if (languageCode) {
      auth.languageCode = languageCode;
    } else {
      // Use device language preference
      auth.useDeviceLanguage();
    }
  } catch (error) {
    console.error("Error setting auth language:", error);
  }
};

/**
 * Re-authenticate a user with their current credentials
 * @param user - The Firebase user object
 * @param currentPassword - The user's current password
 * @returns Promise with success status and optional error message
 */
export const reauthenticateUser = async (
  user: User,
  currentPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {

    if (!user.email) {
      return {
        success: false,
        error: "User email not available for reauthentication",
      };
    }

    // Create credential with email and current password
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );

    // Re-authenticate the user according to Firebase documentation
    await reauthenticateWithCredential(user, credential);

    return { success: true };
  } catch (error: any) {
    console.error("❌ Error re-authenticating user:", error);

    // Provide specific error messages
    let errorMessage = "Failed to re-authenticate";
    if (error.code === "auth/wrong-password") {
      errorMessage = "Current password is incorrect";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage = "Too many failed attempts. Please try again later.";
    } else if (error.code === "auth/user-mismatch") {
      errorMessage = "User credentials do not match";
    } else if (error.code === "auth/user-not-found") {
      errorMessage = "User account not found";
    } else if (error.code === "auth/invalid-credential") {
      errorMessage = "Invalid credentials provided";
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Checks if a user needs to reauthenticate before performing sensitive operations
 * @param user - The Firebase user object
 * @returns boolean indicating if reauthentication is needed
 */
export const needsReauthentication = (user: User): boolean => {
  if (!user.metadata.lastSignInTime) return true;

  const lastSignIn = new Date(user.metadata.lastSignInTime);
  const now = new Date();
  const timeDifferenceInMinutes =
    (now.getTime() - lastSignIn.getTime()) / (1000 * 60);

  // Require reauthentication if last sign-in was more than 5 minutes ago
  return timeDifferenceInMinutes > 5;
};

/**
 * Updates a user's password with proper reauthentication handling
 * @param user - The Firebase user object
 * @param newPassword - The new password
 * @param currentPassword - The current password for reauthentication (required for security)
 * @returns Promise with success status and optional error message
 */
export const updateUserPassword = async (
  user: User,
  newPassword: string,
  currentPassword: string
): Promise<{ success: boolean; error?: string; requiresReauth?: boolean }> => {
  try {

    // Validate password strength
    if (newPassword.length < 6) {
      return {
        success: false,
        error: "Password must be at least 6 characters long",
      };
    }

    // Always require current password for security-sensitive password changes
    if (!currentPassword) {
      return {
        success: false,
        error: "Current password is required to change your password",
        requiresReauth: true,
      };
    }

    // Re-authenticate user before password change (security requirement)
    const reauthResult = await reauthenticateUser(user, currentPassword);
    if (!reauthResult.success) {
      return {
        success: false,
        error: reauthResult.error || "Re-authentication failed",
        requiresReauth: true,
      };
    }

    // Update password according to Firebase documentation
    await updatePassword(user, newPassword);

    return { success: true };
  } catch (error: any) {
    console.error("❌ Error updating password:", error);

    // Provide specific error messages
    let errorMessage = "Failed to update password";
    if (error.code === "auth/weak-password") {
      errorMessage = "Password is too weak. Please choose a stronger password.";
    } else if (error.code === "auth/requires-recent-login") {
      errorMessage =
        "For security, you must sign in again before changing your password.";
      return { success: false, error: errorMessage, requiresReauth: true };
    } else if (error.code === "auth/user-disabled") {
      errorMessage = "This account has been disabled.";
    } else if (error.code === "auth/user-not-found") {
      errorMessage = "User account not found.";
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Sends a verification email to the user using Firebase's built-in method
 * @param user - The Firebase user object
 * @param languageCode - Optional language code for localization
 */
export const sendVerificationEmail = async (
  user: User,
  languageCode: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Set language before sending email
    setAuthLanguage(languageCode);

    // Get action code settings with proper continueUrl
    const actionCodeSettings = getActionCodeSettings();

    // Send verification email with action code settings
    // This ensures the verification link has a proper continueUrl and won't expire prematurely
    await sendEmailVerification(user, actionCodeSettings);

    return { success: true };
  } catch (error: any) {
    console.error("❌ Error sending verification email:", error);

    // Provide specific error messages
    let errorMessage = "Failed to send verification email";
    if (error.code === "auth/too-many-requests") {
      errorMessage =
        "Too many requests. Please wait before requesting another verification email.";
    } else if (error.code === "auth/user-disabled") {
      errorMessage = "This account has been disabled.";
    } else if (error.code === "auth/user-not-found") {
      errorMessage = "User account not found.";
    } else if (error.code === "auth/invalid-continue-uri") {
      errorMessage = "Invalid redirect URL. Please contact support.";
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Resends a verification email to the user
 * @param user - The Firebase user object
 * @param languageCode - Optional language code for localization
 */
export const resendVerificationEmail = async (
  user: User,
  languageCode: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const result = await sendVerificationEmail(user, languageCode);
    return result;
  } catch (error: any) {
    console.error("❌ Error resending verification email:", error);
    return { success: false, error: "Failed to resend verification email" };
  }
};

/**
 * Sends a password reset email to the specified email address
 * @param email - The email address to send the reset link to
 * @param languageCode - Optional language code for localization
 * @returns Promise with success status and optional error message
 */
export const sendPasswordResetEmail = async (
  email: string,
  languageCode: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Set language before sending email
    setAuthLanguage(languageCode);

    // Send password reset email according to Firebase documentation
    await firebaseSendPasswordResetEmail(auth, email);

    return { success: true };
  } catch (error: any) {
    console.error("❌ Error sending password reset email:", error);

    // Provide specific error messages
    let errorMessage = "Failed to send password reset email";
    if (error.code === "auth/user-not-found") {
      errorMessage = "No account found with this email address.";
    } else if (error.code === "auth/invalid-email") {
      errorMessage = "Invalid email address format.";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage =
        "Too many requests. Please wait before requesting another reset email.";
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Get a secure random password (utility function)
 * @param length - Length of the password (default: 12)
 * @returns A secure random password
 */
export const getSecureRandomPassword = (length: number = 12): string => {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";

  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }

  return password;
};

// Initialize with device language by default
setAuthLanguage();
